import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X, Phone, Globe } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

const NavigationHeader = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [promoHeight, setPromoHeight] = useState(0);
  const location = useLocation();
  const { language, changeLanguage } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check if promo bar exists and get its height
  useEffect(() => {
    const checkPromoBar = () => {
      const promoBar = document.querySelector('[data-testid="promo-container"]');
      if (promoBar) {
        setPromoHeight(promoBar.offsetHeight);
      } else {
        setPromoHeight(0);
      }
    };
    
    checkPromoBar();
    // Re-check on resize or when promo might expand/collapse
    window.addEventListener('resize', checkPromoBar);
    const observer = new MutationObserver(checkPromoBar);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      window.removeEventListener('resize', checkPromoBar);
      observer.disconnect();
    };
  }, []);

  const getLocalizedPath = (path) => {
    if (language === 'es') return `/es${path}`;
    if (language === 'fr') return `/fr${path}`;
    return path;
  };

  const navLinks = [
    { name: getTranslation(language, 'nav.clinic'), path: getLocalizedPath('/clinic') },
    { name: getTranslation(language, 'nav.astonVilla'), path: getLocalizedPath('/aston-villa') },
    { name: getTranslation(language, 'nav.englishImmersion'), path: getLocalizedPath('/english-immersion') },
    { name: getTranslation(language, 'nav.technology'), path: getLocalizedPath('/technology') },
    { name: getTranslation(language, 'nav.aboutOakham'), path: getLocalizedPath('/about-oakham') },
    { name: getTranslation(language, 'nav.prices'), path: getLocalizedPath('/prices') },
    { name: getTranslation(language, 'nav.contact'), path: getLocalizedPath('/contact') }
  ];

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' }
  ];

  const currentLanguage = languages.find(lang => lang.code === language);

  const isActive = (path) => location.pathname === path;

  return (
    <header 
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white shadow-lg py-3' 
          : 'bg-white/95 backdrop-blur-sm py-4 shadow-md'
      }`}
      style={{ top: `${promoHeight}px` }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to={getLocalizedPath('/')} className="flex items-center group">
            <div className="flex flex-col">
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 leading-none">
                OAKHAM
              </h1>
              <div className="flex items-center">
                <span className="text-sm md:text-base font-bold text-green-600 uppercase tracking-wider">Football Camps</span>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`px-4 py-2 text-sm font-bold rounded-lg transition-all duration-200 ${
                  isActive(link.path)
                    ? 'text-green-600 bg-green-50'
                    : 'text-gray-700 hover:text-green-600 hover:bg-gray-50'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Language Switcher & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Language Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Globe className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-bold text-gray-700">{currentLanguage.flag}</span>
              </button>
              
              {isLangMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setIsLangMenuOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-2 hover:bg-gray-50 ${
                        language === lang.code ? 'bg-green-50 text-green-600 font-bold' : 'text-gray-700'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link to={getLocalizedPath('/signup')}>
              <Button 
                className="bg-green-600 hover:bg-green-700 text-white font-bold shadow-lg hover:shadow-xl transition-all"
              >
                {getTranslation(language, 'nav.signUp')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6 text-gray-900" />
            ) : (
              <Menu className="h-6 w-6 text-gray-900" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 py-4 bg-white rounded-lg shadow-xl border border-gray-100">
            <nav className="flex flex-col space-y-2 px-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`px-4 py-3 text-base font-bold rounded-lg transition-all ${
                    isActive(link.path)
                      ? 'text-green-600 bg-green-50'
                      : 'text-gray-900 hover:text-green-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Mobile Language Switcher */}
              <div className="border-t pt-4 mt-2">
                <p className="text-xs font-bold text-gray-500 uppercase px-4 mb-2">Language</p>
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-2 rounded-lg ${
                      language === lang.code ? 'bg-green-50 text-green-600 font-bold' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.name}</span>
                  </button>
                ))}
              </div>

              <Link to={getLocalizedPath('/signup')} onClick={() => setIsMobileMenuOpen(false)}>
                <Button 
                  className="bg-green-600 hover:bg-green-700 text-white font-bold w-full mt-2"
                >
                  {getTranslation(language, 'nav.signUp')}
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default NavigationHeader;